<script setup lang="ts">
import { computed } from 'vue';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isDecoration: {
    type: Boolean,
    required: true,
  },
});

const { websiteText } = useWebsiteTexts();
const { formatPrice } = useContext();
const printAddons = computed(() => props.item.addons || []);
const quantity = computed(() => props.item.quantity);

const unitPrice = computed(() => {
  if (props.isDecoration) return props.item.unitPrice;
  if (!props.item.originalPrice) return props.item.currentPrice;
  return props.item.currentPrice < props.item.originalPrice
    ? props.item.currentPrice
    : props.item.originalPrice;
});

const priceSum = computed(() => {
  return unitPrice.value * props.item.quantity;
});

const quantityText = computed(() => {
  return `${quantity.value} ${websiteText('productdetailspage_printpieceabbreviated').value}`;
});

const pricePerItemText = computed(() => {
  return `(${formatPrice(unitPrice.value)}/${websiteText('productdetailspage_printpieceabbreviated').value})`;
});
</script>
<template>
  <div class="cart-price-item">
    <div v-if="!isDecoration" class="cart-price-item__pricing-item">
      <div class="cart-price-item__product-name-and-number">
        <div class="cart-price-item__product-name">
          {{ item.name }}
        </div>
        <div v-if="item.campaignPercentage" class="cart-price-item__campaign">
          (-{{ item.campaignPercentage }}%)
        </div>
        <div v-if="item.articleNumber" class="cart-price-item__details-spacer"></div>
        <div class="cart-price-item__article-number">
          {{ item.articleNumberForDisplay }}
        </div>
      </div>
      <div class="cart-price-item__quantity-and-price">
        <div class="cart-price-item__quantity">
          {{quantityText}} {{pricePerItemText}}
        </div>
        <div class="cart-price-item__price">
          {{ formatPrice(priceSum) }}
        </div>
      </div>
    </div>
    <div v-if="isDecoration" class="cart-price-item__pricing-item">
      <div class="cart-price-item__product-name-and-number">
        <div class="cart-price-item__decoration-name">
          {{ item.name || 'DecorationName' }}
        </div>
      </div>
      <div
        class="cart-price-item__product-fee"
        v-for="printAddon in printAddons"
        :key="printAddon.id"
      >
        <div class="cart-price-item__product-fee-name">
          {{printAddon.name}}
        </div>
        <div class="cart-price-item__price">
          {{formatPrice(printAddon.unitPrice)}}
        </div>
      </div>
      <div class="cart-price-item__quantity-and-price">
        <div class="cart-price-item__quantity">
          {{quantityText}} {{pricePerItemText}}
        </div>
        <div class="cart-price-item__price">
          {{ formatPrice(priceSum) }}
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.cart-price-item {
  padding-left: 10px;
}

.cart-price-item__pricing-item {
  font-size: 14px;
  line-height: 26px;
  color: var(--color-text-darker);
}

.cart-price-item__product-name-and-number {
  font-size: 12px;
  letter-spacing: 0.18px;
  line-height: 17px;
  display: flex;
  align-items: center;
}

.cart-price-item__product-name,
.cart-price-item__decoration-name {
  font-weight: 700;
}

.cart-price-item__campaign {
  color: var(--color-feedback-error);
  margin-left: 4px;
}

.cart-price-item__details-spacer {
  margin: 0 6px;
  height: 12px;
  border-left: 1px solid #E5E5E5;
}

.cart-price-item__quantity-and-price,
.cart-price-item__product-fee {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  line-height: 17px;
}

.cart-price-item__quantity {
  color: var(--color-neutrals-80);
}

.cart-price-item__quantity-amount {
  font-size: 13px;
  line-height: 22px;
}
</style>
